import { defineStore } from "pinia";
import axios from "@/utils/axios";
import { useSharedStore } from "@/stores/shared-store";
import { useAuthStore } from "@/stores/auth-store";

export const useCourseFetchStore = defineStore("CourseList", {
  state: () => ({
    sharedStore: useSharedStore(),
    authStore: useAuthStore(),
    courseList: [],
    groupList: [],
    difficultyList: [],
    statusList: [],
    basicCourseDetail: {},

    filterGroup: [],
    filterDifficulties: [],
    filterInstructor: [],
    filterStatus: [],
    searchKeyword: "",

    courseCreatedList: [],
    studentQuisSubmition: [],

    pagination: {
      currentPage: 1,
      pageSize: 12, // atau 5, 20, dll
    },
  }),
  getters: {
    paginatedCourses(state) {
      const totalPages = Math.max(
        1,
        Math.ceil(state.filteredCourses.length / state.pagination.pageSize)
      );
      const safePage = Math.min(
        Math.max(1, state.pagination.currentPage),
        totalPages
      );
      const start = (safePage - 1) * state.pagination.pageSize;
      const end = start + state.pagination.pageSize;
      return state.filteredCourses.slice(start, end);
    },
    totalPages(state) {
      return Math.max(
        1,
        Math.ceil(state.filteredCourses.length / state.pagination.pageSize)
      );
    },
    filteredCount(state) {
      return state.filteredCourses.length;
    },
    hasActiveFilters(state) {
      return (
        state.filterGroup.length > 0 ||
        state.filterDifficulties.length > 0 ||
        state.filterInstructor.length > 0 ||
        state.filterStatus.length > 0 ||
        !!state.searchKeyword?.trim()
      );
    },
    filteredCourses(state) {
      return state.courseList
        .filter(
          (course) =>
            course.status === "Active" ||
            course.status === "Need Approval Edit"
        )
        .filter((course) => {
          const matchGroup =
            state.filterGroup.length === 0 ||
            state.filterGroup.some((f) => {
              if (f == null) return false;
              const fStr = String(f).trim().toLowerCase();

              // Cek ID di objek course_group atau course_group_id
              const gId =
                course.course_group?.id ??
                course.course_group?.course_group_id ??
                course.course_group_id;
              if (gId != null && String(gId).trim().toLowerCase() === fStr) return true;

              // Jika course.course_group berupa nilai langsung (angka/string ID)
              if (
                typeof course.course_group === "number" ||
                (typeof course.course_group === "string" && !course.course_group.includes("{"))
              ) {
                if (String(course.course_group).trim().toLowerCase() === fStr) return true;
              }

              // Cek juga terhadap code atau name dari course_group
              if (
                course.course_group?.code &&
                String(course.course_group.code).trim().toLowerCase() === fStr
              )
                return true;
              if (
                course.course_group?.name &&
                String(course.course_group.name).trim().toLowerCase() === fStr
              )
                return true;

              return false;
            });

          const matchDifficulty =
            state.filterDifficulties.length === 0 ||
            state.filterDifficulties.some(
              (d) => String(d).toLowerCase() === String(course.level).toLowerCase()
            );

          const matchInstructor =
            state.filterInstructor.length === 0 ||
            state.filterInstructor.some(
              (i) => String(i) === String(course.instructor)
            );

          const matchStatus =
            state.filterStatus.length === 0 ||
            state.filterStatus.includes(course.status);

          const matchKeyword =
            !state.searchKeyword ||
            course.title
              ?.toLowerCase()
              .includes(state.searchKeyword.toLowerCase());

          return (
            matchGroup &&
            matchDifficulty &&
            matchInstructor &&
            matchKeyword &&
            matchStatus
          );
        });
    },
    filteredAllCourses(state) {
      return state.courseList.filter((course) => {
        const matchGroup =
          state.filterGroup.length === 0 ||
          state.filterGroup.some((f) => {
            if (f == null) return false;
            const fStr = String(f).trim().toLowerCase();

            const gId =
              course.course_group?.id ??
              course.course_group?.course_group_id ??
              course.course_group_id;
            if (gId != null && String(gId).trim().toLowerCase() === fStr) return true;

            if (
              typeof course.course_group === "number" ||
              (typeof course.course_group === "string" && !course.course_group.includes("{"))
            ) {
              if (String(course.course_group).trim().toLowerCase() === fStr) return true;
            }

            if (
              course.course_group?.code &&
              String(course.course_group.code).trim().toLowerCase() === fStr
            )
              return true;
            if (
              course.course_group?.name &&
              String(course.course_group.name).trim().toLowerCase() === fStr
            )
              return true;

            return false;
          });

        const matchDifficulty =
          state.filterDifficulties.length === 0 ||
          state.filterDifficulties.some(
            (d) => String(d).toLowerCase() === String(course.level).toLowerCase()
          );

        const matchInstructor =
          state.filterInstructor.length === 0 ||
          state.filterInstructor.some(
            (i) => String(i) === String(course.instructor)
          );

        const matchStatus =
          state.filterStatus.length === 0 ||
          state.filterStatus.includes(course.status);

        const matchKeyword =
          !state.searchKeyword ||
          course.title
            ?.toLowerCase()
            .includes(state.searchKeyword.toLowerCase());

        return (
          matchGroup &&
          matchDifficulty &&
          matchInstructor &&
          matchKeyword &&
          matchStatus
        );
      });
    },
    paginatedAllCourses(state) {
      const totalPages = Math.max(
        1,
        Math.ceil(state.filteredAllCourses.length / state.pagination.pageSize)
      );
      const safePage = Math.min(
        Math.max(1, state.pagination.currentPage),
        totalPages
      );
      const start = (safePage - 1) * state.pagination.pageSize;
      const end = start + state.pagination.pageSize;
      return state.filteredAllCourses.slice(start, end);
    },
    totalAllPages(state) {
      return Math.max(
        1,
        Math.ceil(state.filteredAllCourses.length / state.pagination.pageSize)
      );
    },
    unapprovedCourses(state) {
      return state.courseList.filter(
        (course) =>
          course.status === "New" ||
          course.status === "Need Approval Edit"
      );
    },
    getCourseTitleById: (state) => (id) => {
      const course = state.courseList.find((course) => course.course_id === id);
      return course ? course.title : null;
    },
  },
  actions: {
    setSearchKeyword(keyword) {
      this.searchKeyword = keyword;
      this.pagination.currentPage = 1; // Reset ke halaman 1 setelah pencarian
    },
    clearAllFilters() {
      this.filterGroup = [];
      this.filterDifficulties = [];
      this.filterInstructor = [];
      this.filterStatus = [];
      this.searchKeyword = "";
      this.pagination.currentPage = 1;
    },

    //perbaiki semua url di bawah
    async fetchCourseList() {
      this.sharedStore.startRequest();
      try {
        const response = await axios.get(
          `${this.authStore.Url}/api/courses/manage`
        );
        this.courseList = response.data || [];
      } catch (error) {
        console.log(error);
      } finally {
        this.sharedStore.endRequest();
      }
    },
    async fetchDifficultyList() {
      this.sharedStore.startRequest();
      try {
        const response = await axios.get(
          `${this.authStore.Url}/api/courses/manage/levels`,
          {
            headers: {
              Authorization: `Bearer ${this.authStore.token}`,
            },
          }
        );
        this.difficultyList = response.data;
      } catch (error) {
        console.log(error);
      } finally {
        this.sharedStore.endRequest();
      }
    },
    async fetchCourseByCreatorList() {
      this.sharedStore.startRequest();
      try {
        const instructorId = this.authStore.currentUser?.id;
        const response = await axios.get(
          `${this.authStore.Url}/api/courses/manage/`,
          {
            params: { instructor: instructorId },
          }
        );
        const courses = response.data || [];
        this.courseCreatedList = courses;
        // Keep list pages/filters in sync with creator-scoped data
        this.courseList = courses;
        this.pagination.currentPage = 1;
      } catch (error) {
        console.log(error);
      } finally {
        this.sharedStore.endRequest();
      }
    },

    //Detail Course
    async fetchCourseDetailBasic(id) {
      if (id === undefined || id === null || id === "" || id === "undefined") {
        console.error("fetchCourseDetailBasic: invalid course id", id);
        return null;
      }

      this.sharedStore.startRequest();
      try {
        const response = await axios.get(
          `${this.authStore.Url}/api/courses/manage/${id}`
        );
        this.basicCourseDetail = response.data;
        return response.data;
      } catch (error) {
        console.error("Gagal mengambil detail course:", id, error);
        // Jangan kosongkan detail yang sudah di-seed (mis. dari enrollment)
        // supaya resume/watch tetap bisa tampil.
        if (!this.basicCourseDetail?.course_id) {
          this.sharedStore.showError(
            "Gagal memuat detail course. Coba refresh halaman."
          );
        }
        return null;
      } finally {
        this.sharedStore.endRequest();
      }
    },

    setBasicCourseDetail(course) {
      if (course && typeof course === "object") {
        this.basicCourseDetail = { ...course };
      }
    },

    /* Group */
    async fetchGroupList() {
      this.sharedStore.startRequest();
      try {
        const response = await axios.get(
          `${this.authStore.Url}/api/courses/course-groups/`
        );
        this.groupList = response.data;
      } catch (error) {
        console.log(error);
      } finally {
        this.sharedStore.endRequest();
      }
    },
    async fetchStatusList() {
      this.sharedStore.startRequest();
      try {
        const response = await axios.get(
          `${this.authStore.Url}/api/courses/manage/status`,
          {
            headers: {
              Authorization: `Bearer ${this.authStore.token}`,
            },
          }
        );
        this.statusList = response.data;
      } catch (error) {
        console.log(error);
      } finally {
        this.sharedStore.endRequest();
      }
    },
    async fetchStudentQuizSubmission(studentId, courseId) {
      this.sharedStore.startRequest();
      try {
        // Misal endpointnya quiz-submission dan bisa filter student dan course seperti ini
        const response = await axios.get(
          `${this.authStore.Url}/api/courses/quiz-submission/?student=${studentId}&quiz__content__courseID=${courseId}`
        );
        this.studentQuisSubmition = response.data;
      } catch (error) {
        console.log("Gagal fetch quiz submission:", error);
      } finally {
        this.sharedStore.endRequest();
      }
    },
  },
});
